/**
 * Tournament results component showing final rankings with map and bracket views.
 */

import { GitBranch, List, Medal, RotateCcw, Trophy, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { RatingStars } from '@/components/RatingStars';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WorldMap } from '@/components/WorldMap';
import { getCountryByCode, getScopeById } from '@/data/countries';
import { rankingsToRatings } from '@/lib/tournament';
import { cn } from '@/lib/utils';
import { BracketViewCanvas, BracketViewMobile } from './BracketView';
import { useTournament } from './TournamentProvider';

export function TournamentResults() {
  const { state, actions } = useTournament();
  const [activeTab, setActiveTab] = useState<'list' | 'bracket'>('list');
  const [showCompletionModal, setShowCompletionModal] = useState(true);

  // Convert tournament rankings to ratings for the map
  const tournamentRatings = useMemo(() => {
    if (!state?.finalRankings) return {};
    return rankingsToRatings(state.finalRankings);
  }, [state?.finalRankings]);

  // Get ALL scope country codes for the map (not just tournament participants)
  // Countries not in the tournament will appear gray (no rating)
  const scopeCountryCodes = useMemo(() => {
    if (!state?.scopeId) return [];
    const scope = getScopeById(state.scopeId);
    return scope?.countryCodes ?? [];
  }, [state?.scopeId]);

  // Get scope name for export
  const scopeName = useMemo(() => {
    if (!state?.scopeId) return undefined;
    return getScopeById(state.scopeId)?.name;
  }, [state?.scopeId]);

  if (!state || state.status !== 'completed' || !state.finalRankings) {
    return null;
  }

  const rankings = state.finalRankings;
  const winner = rankings[0];
  const winnerCountry = winner ? getCountryByCode(winner.code) : null;

  // Get position icon
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return (
          <span className="w-5 text-center font-bold text-muted-foreground">
            {position}
          </span>
        );
    }
  };

  return (
    <>
      {/* Completion Modal - auto-shows when tournament finishes */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="text-center shrink-0">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
            <DialogTitle className="text-xl">Tournament Complete!</DialogTitle>
            {winnerCountry && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-3xl">{winnerCountry.flag}</span>
                <span className="text-lg font-semibold">{winnerCountry.name}</span>
                <span className="text-sm text-muted-foreground">is your champion!</span>
              </div>
            )}
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden -mx-6 px-6">
            <WorldMap
              ratings={tournamentRatings}
              scopeCountryCodes={scopeCountryCodes}
              scopeId={state.scopeId}
              mode="tournament"
              scopeName={scopeName}
            />
          </div>

          <div className="shrink-0 pt-4">
            <Button
              onClick={() => setShowCompletionModal(false)}
              className="w-full"
            >
              View Full Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="h-full flex flex-col overflow-hidden">
        {/* Compact header with tabs */}
        <div className="shrink-0 px-3 pt-2 pb-1 border-b bg-background">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <Trophy className="w-4 h-4 text-yellow-500" />
          <h2 className="text-sm font-display font-bold">
            Tournament Complete!
          </h2>
          <span className="text-xs text-muted-foreground">
            ({rankings.length} countries)
          </span>
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'list' | 'bracket')}
        >
          <TabsList className="w-full h-8">
            <TabsTrigger value="list" className="flex-1 gap-1 text-xs h-7">
              <List className="w-3.5 h-3.5" />
              Rankings
            </TabsTrigger>
            <TabsTrigger value="bracket" className="flex-1 gap-1 text-xs h-7">
              <GitBranch className="w-3.5 h-3.5" />
              Bracket
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Main content area - flex-1 h-0 forces flex to respect parent bounds */}
      <div
        className={cn(
          'flex-1 h-0',
          activeTab === 'list'
            ? 'overflow-y-auto overscroll-contain'
            : 'flex flex-col',
        )}
      >
        {activeTab === 'list' ? (
          <div className="p-2 space-y-2">
            {/* Map Section - uses WorldMap with pan/zoom and share controls */}
            <WorldMap
              ratings={tournamentRatings}
              scopeCountryCodes={scopeCountryCodes}
              scopeId={state.scopeId}
              mode="tournament"
              scopeName={scopeName}
            />

            {/* Rankings List */}
            <Card>
              <CardHeader className="pb-1 pt-2 px-2">
                <h3 className="font-semibold text-sm">Final Rankings</h3>
              </CardHeader>
              <CardContent className="px-2 pb-2">
                <div className="space-y-0.5">
                  {rankings.map((ranking) => {
                    const country = getCountryByCode(ranking.code);
                    if (!country) return null;

                    return (
                      <div
                        key={ranking.code}
                        className={cn(
                          'flex items-center gap-1.5 py-1.5 px-2 rounded-md transition-colors',
                          ranking.position <= 3 && 'bg-muted/50',
                        )}
                      >
                        <div className="w-5 flex justify-center shrink-0">
                          {getPositionIcon(ranking.position)}
                        </div>
                        <span className="text-lg shrink-0">{country.flag}</span>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-xs truncate block">
                            {country.name}
                          </span>
                        </div>
                        <RatingStars rating={ranking.rating} size="sm" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Bracket View - fills available height */
          <Card className="flex-1 flex flex-col m-2 overflow-hidden">
            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
              {/* Desktop/Tablet: Canvas view with pan/zoom */}
              <div className="hidden sm:flex flex-1 min-h-0">
                {state?.bracket && (
                  <BracketViewCanvas
                    bracket={state.bracket}
                    className="h-full w-full"
                  />
                )}
              </div>
              {/* Mobile: Vertical stacked view */}
              <div className="sm:hidden flex-1 min-h-0">
                {state?.bracket && (
                  <BracketViewMobile
                    bracket={state.bracket}
                    className="h-full w-full"
                  />
                )}
              </div>
              <p className="shrink-0 text-xs text-muted-foreground text-center py-2 border-t">
                <span className="hidden sm:inline">
                  Scroll to zoom, drag to pan, click a country to highlight path
                </span>
                <span className="sm:hidden">Scroll to see all rounds</span>
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Actions footer */}
      <div className="shrink-0 p-2 border-t bg-background">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="w-full h-8 text-xs" size="sm">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Start New Tournament
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Start new tournament?</AlertDialogTitle>
              <AlertDialogDescription>
                This will clear these results and start a fresh tournament. Your
                current rankings will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Results</AlertDialogCancel>
              <AlertDialogAction onClick={actions.abandonTournament}>
                Start New
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      </div>
    </>
  );
}
